import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

// Histórico de Retenção (fev/2026):
// Mostra o log de arquivos de backup apagados automaticamente pela política de retenção.
// SA vê tudo, tenants veem apenas da própria empresa.
// props: showEmpresaColumn=true quando o consumidor for SA (BackupVault).

const fmtSize = (b) => !b ? "—" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;
const fmtDate = (i) => { if (!i) return "—"; try { return new Date(i).toLocaleString("pt-BR"); } catch { return i; } };

export function RetentionLog({ showEmpresaColumn = false, defaultOpen = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/backups/retention-log?limit=200");
      setEntries(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar histórico de retenção");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  return (
    <Card data-testid="retention-log-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-rose-600" />
            Histórico de Retenção
            {entries.length > 0 && <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]">{entries.length}</Badge>}
          </CardTitle>
          <div className="flex gap-2">
            {open && (
              <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-retention">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Atualizar
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} data-testid="btn-toggle-retention">
              {open ? <><ChevronUp className="w-3.5 h-3.5 mr-1" />Ocultar</> : <><ChevronDown className="w-3.5 h-3.5 mr-1" />Mostrar</>}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Arquivos apagados automaticamente pela política de retenção. O mais recente de cada empresa é sempre preservado.
        </p>
      </CardHeader>
      {open && (
        <CardContent className="p-0">
          {loading && entries.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="retention-loading">Carregando…</div>
          )}
          {!loading && entries.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="retention-empty">
              <Trash2 className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              Nenhum backup foi apagado ainda pela retenção
            </div>
          )}
          {entries.length > 0 && (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-semibold">Arquivo</th>
                    {showEmpresaColumn && <th className="px-4 py-2 font-semibold">Empresa</th>}
                    <th className="px-4 py-2 font-semibold">Tamanho</th>
                    <th className="px-4 py-2 font-semibold">Backup Original</th>
                    <th className="px-4 py-2 font-semibold">Apagado em</th>
                    <th className="px-4 py-2 font-semibold">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-slate-50/50" data-testid={`retention-row-${e.id}`}>
                      <td className="px-4 py-2 font-mono text-[11px]">{e.filename}</td>
                      {showEmpresaColumn && (
                        <td className="px-4 py-2 text-xs font-semibold text-indigo-700">{e.empresa_nome || "—"}</td>
                      )}
                      <td className="px-4 py-2 text-xs whitespace-nowrap">{fmtSize(e.size_bytes)}</td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap text-slate-500">{fmtDate(e.backup_started_at)}</td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap font-semibold">{fmtDate(e.deleted_at)}</td>
                      <td className="px-4 py-2">
                        {e.source === "auto" ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">Auto ({e.retention_days}d)</Badge>
                        ) : (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">Manual ({e.retention_days}d)</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
